// The shell every public route (including /cave/*) renders inside: the
// page-turn sweep overlay, the centred header, <main>, and the cloth footer
// banner. Owns the page-turn click interception (PROTO src/site.js
// `leave`/`arrive`, tightened per build-plan R1), the per-route document
// title, and toggling `html.dark` for the Hidden Treasure Cave.
import { useEffect, useLayoutEffect, useRef } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { BannerCanvas } from './BannerCanvas.js';
import { SiteHeader } from './SiteHeader.js';
import { reduceMotion, resetScrollForRouteChange, useMotion } from './motion.js';

const TITLES: Record<string, string> = {
  '/': 'Clan Heart',
  '/about': 'About Clan Heart',
  '/join': 'Join our ranks',
  '/code': 'Pirating code of honor',
  '/leaving': 'Ending your clan membership',
  '/lore': 'Epic Clan Lore',
  '/news': 'Public News Paper',
};

/** Routes the page-turn sweep is allowed to cover — same-origin internal
 * navigation only, and only to these exact pathnames (build-plan R1).
 * `/cave` itself turns the page; anything under it (`/cave/leaderboard`,
 * etc.) is the signed-in app and must not. */
const SWEEPABLE = new Set(['/', '/about', '/join', '/code', '/lore', '/news', '/leaving', '/cave']);

const ARRIVE_MS = 900; // matches site.js's sweepOut hold before cleanup
const LEAVE_MS = 420; // matches site.js's leave->navigate delay

function isCaveRoute(pathname: string): boolean {
  return pathname === '/cave' || pathname.startsWith('/cave/');
}

export function PublicLayout() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const sweepingRef = useRef(false);
  const arriveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useMotion();

  // Page-turn: intercept clicks on internal links to another sweepable
  // route, play the sweep in, then navigate once it has covered the screen.
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (reduceMotion) return;
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      const target = e.target as HTMLElement | null;
      const anchor = target?.closest?.('a[href]') as HTMLAnchorElement | null;
      if (!anchor || anchor.target === '_blank') return;
      const rawHref = anchor.getAttribute('href') ?? '';
      if (rawHref.startsWith('#')) return;
      let url: URL;
      try {
        url = new URL(anchor.href, window.location.href);
      } catch {
        return;
      }
      if (url.origin !== window.location.origin) return;
      if (url.hash) return;
      const path = url.pathname;
      if (!SWEEPABLE.has(path)) return;
      if (path === window.location.pathname) return;
      // moving between the members' own tabs is not a page turn
      if (path === '/cave' && window.location.pathname.startsWith('/cave/')) return;
      e.preventDefault();
      sweepingRef.current = true;
      document.documentElement.classList.add('leave');
      timer = window.setTimeout(() => {
        navigate(path);
      }, LEAVE_MS);
    }
    let timer: number | undefined;
    document.addEventListener('click', handleClick, true);
    return () => {
      document.removeEventListener('click', handleClick, true);
      window.clearTimeout(timer);
    };
  }, [navigate]);

  // Once the route has actually changed, either play the sweep out (if we
  // were the ones who triggered it) or do nothing — back/forward never
  // sweeps (build-plan R1).
  useLayoutEffect(() => {
    resetScrollForRouteChange();
    if (sweepingRef.current) {
      sweepingRef.current = false;
      document.documentElement.classList.remove('leave');
      if (!reduceMotion) {
        document.documentElement.classList.add('arrive');
        arriveTimerRef.current = setTimeout(() => {
          document.documentElement.classList.remove('arrive');
        }, ARRIVE_MS);
      }
    }
    return () => {
      if (arriveTimerRef.current) clearTimeout(arriveTimerRef.current);
    };
  }, [pathname]);

  useEffect(() => {
    document.title = isCaveRoute(pathname) ? 'Hidden Treasure Cave' : (TITLES[pathname] ?? 'Clan Heart');
  }, [pathname]);

  useEffect(() => {
    document.documentElement.classList.toggle('dark', isCaveRoute(pathname));
  }, [pathname]);

  const cave = isCaveRoute(pathname);
  const hideFooter = pathname === '/' || cave;

  return (
    <>
      <div className="sweep" aria-hidden="true" />
      <SiteHeader />
      {cave ? (
        <main className="cave-main">
          <BannerCanvas kind="cave" />
          <Outlet />
        </main>
      ) : (
        <main>
          <Outlet />
        </main>
      )}
      {!hideFooter && (
        <footer className="banner-foot" aria-hidden="true">
          <BannerCanvas kind="cave" />
        </footer>
      )}
    </>
  );
}
